import { pageWidth } from '../constants/gridConfig';

// Bandes « saut de page » de l'éditeur : un espace gris entre les pages pleines
// (slides en paysage, masques pleine page et anciennes pages en portrait) et ce
// qui les entoure. Purement visuel : le PDF ne passe pas par l'éditeur.

/** Bande au-dessus d'une page placée en tête : même espace que le haut du document. */
export const FIRST_PAGE_BREAK_HEIGHT = 35;

/**
 * Hauteur d'une bande, en px avant zoom : celle de la barre d'ajout entre deux
 * containers (AdderColumns), haute de `lineSpacing` et posée juste au-dessus du
 * container. Au survol, le bleu recouvre ainsi toute la bande sans déborder sur
 * les pages, quelle que soit la « Marge entre les lignes » du document.
 *
 * En tête de document, rien au-dessus à séparer : la bande garde l'espace
 * habituel du haut de page, la barre venant se loger dans son bas.
 */
export const pageBreakHeight = ({ lineSpacing, isDocumentTop = false }) =>
  isDocumentTop ? Math.max(FIRST_PAGE_BREAK_HEIGHT, lineSpacing) : lineSpacing;

/** Ombre d'une page pleine, pour qu'elle se lise comme une feuille. */
export const PAGE_SHADOW = '0 2px 8px rgba(0, 0, 0, 0.18)';

/**
 * Le container occupe-t-il une page entière ?
 *
 * - masque pleine page (page de garde, séparation de chapitres…) et ancienne
 *   page : toujours, quelle que soit l'orientation ;
 * - en paysage, un container dessin haut d'une page : un slide. Les variables
 *   de sélection, elles, y gardent une petite hauteur et restent dans le flux.
 *
 * Le contenu courant (textes, titres, box dessin du portrait) n'en est pas : ses
 * vrais sauts de page ne sont connus qu'à la génération du PDF.
 */
export const isFullPageContainer = ({ container, column, landscape }) => {
  if (!column) return false;
  if (column.maskId || column.isOldPage) return true;
  return !!landscape && !!column.drawMode && container?.height === pageWidth;
};

/**
 * Pour chaque item de la page infinie : bande avant, bande après, et s'il faut
 * le laisser sur le fond gris plutôt que sur du blanc.
 *
 * - Une page a toujours une bande après elle, et une bande avant sauf si l'item
 *   visible précédent est déjà une page : deux pages ne sont séparées que par
 *   une seule bande.
 * - Les items masqués (sous un titre replié) ne comptent pas comme voisins. Ceux
 *   qui suivent une page restent sur le fond gris, sinon leurs espacements
 *   feraient des rayures blanches dans la bande.
 */
export const computePageBreaks = (items) => {
  let previousVisibleIsPage = false;
  return items.map((item) => {
    if (item?.type === 'title_container_hidden') {
      return { before: false, after: false, onBackground: previousVisibleIsPage };
    }
    const isPage = !!item?.isPage;
    const breaks = { before: isPage && !previousVisibleIsPage, after: isPage, onBackground: false };
    previousVisibleIsPage = isPage;
    return breaks;
  });
};

/**
 * Le fond blanc de l'item doit-il déborder d'1px sur le suivant ?
 *
 * Le document est affiché avec un `zoom` CSS et la liste virtuelle place ses
 * items à des positions fractionnaires : Chrome arrondit séparément le bas du
 * blanc d'un item et le haut du suivant, et il reste parfois une ligne de
 * pixels sans fond, où le gris du canevas passe (un trait gris sous un bloc).
 * Le débord la couvre ; le suivant, peint après, garde son contenu par-dessus.
 * Pas de débord vers une bande de saut de page ni vers un item laissé sur le
 * fond gris : le blanc y ferait lui-même un trait.
 */
export const coversSeamBelow = (breaks, index) => {
  const current = breaks[index];
  const next = breaks[index + 1];
  return !!current && !!next &&
    !current.onBackground && !current.after && !next.before && !next.onBackground;
};

export const SEAM_COVER_SHADOW = '0 1px 0 white';
